/**
 * Cart Page & Drawer - Self-contained (no external dependencies)
 */

// Helper: Debounce function
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Helper: Fetch config
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

// Constants
const ON_CHANGE_DEBOUNCE_TIMER = 300;

// Simple pub/sub
const subscribers = {};
function subscribe(eventName, callback) {
  if (!subscribers[eventName]) subscribers[eventName] = [];
  subscribers[eventName].push(callback);
  return () => {
    subscribers[eventName] = subscribers[eventName].filter(cb => cb !== callback);
  };
}
function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach(callback => callback(data));
  }
}
const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update'
};

// Trap focus helper (simplified)
function trapFocus(container, elementToFocus) {
  if (elementToFocus) elementToFocus.focus();
}

// Cart Remove Button
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (cartItems && cartItems.updateQuantity) {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

// Cart Items (for cart page)
class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    fetch('/cart?section_id=main-cart-items')
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty) this.innerHTML = sourceQty.innerHTML;
      })
      .catch(e => console.error(e));
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents'
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section).filter(Boolean),
      sections_url: window.location.pathname
    });

    fetch(window.routes?.cart_change_url || '/cart/change.js', { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const element = document.getElementById(section.id);
          if (!element || !parsedState.sections?.[section.section]) return;
          const elementToReplace = element.querySelector(section.selector) || element;
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement?.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings?.error || 'Error updating cart';
          } else {
            message = (window.cartStrings?.quantityError || 'Quantity error').replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          lineItem.querySelector(`[name="${name}"]`).focus();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings?.error || 'Error updating cart';
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) errorText.innerHTML = message;
    }

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement?.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.add('hidden'));
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

// Cart Note
if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(window.routes?.cart_update_url || '/cart/update.js', { ...fetchConfig(), body });
      }, ON_CHANGE_DEBOUNCE_TIMER));
    }
  });
}

// Quantity Input (for cart page)
if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });

      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    onButtonClick(event) {
      event.preventDefault();
      const previousValue = this.input.value;
      // Use closest('button') to get the actual button element, not the SVG icon inside it
      const button = event.target.closest('button');
      const buttonName = button ? button.getAttribute('name') : event.target.name;
      buttonName === 'plus' ? this.input.stepUp() : this.input.stepDown();
      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }
  });
}

// Cart Drawer Custom Element
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('#CartDrawer-Overlay');

    // Close when clicking overlay
    this.overlay?.addEventListener('click', () => this.close());

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('open')) {
        this.close();
      }
    });

    // Listen for cart:refresh event (dispatched by essential accessories add)
    document.addEventListener('cart:refresh', () => {
      this.refreshDrawer();
    });
  }

  refreshDrawer() {
    // Fetch fresh cart content using the cart-drawer-content section (reliable section rendering)
    fetch('/cart?section_id=cart-drawer-content')
      .then(res => res.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newContent = doc.querySelector('#cart-drawer-content-section');

        if (!newContent) {
          console.error('Cart drawer content section not found');
          return;
        }

        // Check if cart is empty from the new content
        const itemCount = parseInt(newContent.dataset.itemCount || '0', 10);
        const isEmpty = itemCount === 0;

        // Update empty state on drawer
        this.classList.toggle('is-empty', isEmpty);

        // Update cart-drawer-items empty state
        const cartDrawerItems = this.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          cartDrawerItems.classList.toggle('is-empty', isEmpty);
        }

        // Update the cart items container with fresh content
        const targetContainer = this.querySelector('#CartDrawer-CartItems');
        if (targetContainer && !isEmpty) {
          // Replace the entire contents with fresh HTML
          targetContainer.innerHTML = newContent.innerHTML;
        }

        // Update cart icon bubble count
        const bubbles = document.querySelectorAll('.cart-count-bubble');
        bubbles.forEach(bubble => {
          bubble.textContent = itemCount;
          bubble.style.display = itemCount > 0 ? 'flex' : 'none';
        });

        // Enable/disable checkout button
        const checkoutBtn = this.querySelector('#CartDrawer-Checkout');
        if (checkoutBtn) {
          checkoutBtn.disabled = isEmpty;
        }

        // Trigger price recalculation for bundle discounts
        if (window.updateCartDrawerBundlePrices) {
          setTimeout(() => window.updateCartDrawerBundlePrices(), 200);
        }
      })
      .catch(err => console.error('Cart drawer refresh error:', err));
  }

  open() {
    this.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.classList.remove('open');
    document.body.style.overflow = '';
  }

  renderContents(response) {
    // Refresh cart drawer contents after add to cart using reliable section rendering
    this.refreshDrawer();

    // Update empty state
    this.classList.remove('is-empty');
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      cartDrawerItems.classList.remove('is-empty');
    }

    // Enable checkout button
    const checkoutBtn = this.querySelector('#CartDrawer-Checkout');
    if (checkoutBtn) {
      checkoutBtn.disabled = false;
    }

    // Open the drawer
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '.drawer__cart-items-wrapper'
      }
    ];
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// Cart Drawer Items Custom Element
class CartDrawerItems extends CartItems {
  constructor() {
    super();
    // Add event delegation for dynamically loaded remove buttons
    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('cart-remove-button');
      if (removeBtn) {
        e.preventDefault();
        const index = removeBtn.dataset.index;
        if (index) {
          this.updateQuantity(index, 0);
        }
      }
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer-content',
        selector: '#cart-drawer-content-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
